import { useState } from 'react'
import { router } from '@inertiajs/react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/Dialog'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { plan_product_pricings_price_levels_path } from '@/lib/routes'
import { useMarket } from '@/lib/useMarket'
import type { PriceLevel } from './PricingTable'
import PriceLevelRow from './PriceLevelRow'

const DEFAULT_COLOR = '#cccccc'

interface Props {
  planSlug: string
  channelSlug: string
  channelName: string
  levels: PriceLevel[]
  open: boolean
  onClose: () => void
}

// Add/rename/remove price levels for one (plan, channel) pair. Levels are
// plan-and-channel scoped (Task 3), so this modal never touches other
// channels or plans.
export default function ManagePriceLevelsModal({ planSlug, channelSlug, channelName, levels, open, onClose }: Props) {
  const market = useMarket()
  const [name, setName] = useState('')
  const [color, setColor] = useState(DEFAULT_COLOR)
  const [adding, setAdding] = useState(false)

  function addLevel() {
    if (!name.trim() || adding) return
    setAdding(true)
    router.post(
      plan_product_pricings_price_levels_path(market.slug, planSlug, channelSlug),
      { price_level: { name: name.trim(), color } },
      {
        preserveScroll: true,
        onSuccess: () => {
          setName('')
          setColor(DEFAULT_COLOR)
        },
        onFinish: () => setAdding(false),
      },
    )
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Price levels — {channelName}</DialogTitle>
          <DialogDescription>These levels apply to this channel in this plan only.</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-2">
          {levels.length === 0 && (
            <p className="text-[13px] text-muted-foreground">No price levels yet. Add one below.</p>
          )}
          {levels.map((level) => (
            <PriceLevelRow
              key={level.id}
              level={level}
              marketSlug={market.slug}
              planSlug={planSlug}
              channelSlug={channelSlug}
              channelName={channelName}
            />
          ))}
        </div>

        <div className="flex items-center gap-2 border-t pt-3">
          <input
            type="color"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            aria-label="New level color"
            className="h-9 w-10 shrink-0 cursor-pointer rounded-md border border-input bg-background p-1"
          />
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Flash Sale"
            onKeyDown={(e) => {
              if (e.key === 'Enter') addLevel()
            }}
            className="flex-1"
          />
          <Button size="sm" onClick={addLevel} disabled={!name.trim() || adding}>
            {adding ? 'Adding…' : 'Add level'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
